import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import { If } from 'utils';
import { UiKit } from 'components';
import { useId } from 'utils/hooks';

/**
 * @typedef {Object} CheckboxProps
 * @property {string} [id] - Unique identifier for the checkbox input
 * @property {React.ReactNode} [label] - Label text or component to display next to checkbox
 * @property {React.ReactNode} [caption] - Additional caption text displayed below label
 * @property {string} [tooltipText] - Tooltip text for the label
 * @property {boolean} [checked=false] - Whether the checkbox is checked
 * @property {boolean} [disabled=false] - Whether the checkbox is disabled
 * @property {boolean} [indeterminate=false] - Whether the checkbox is in indeterminate state
 * @property {(value: boolean, event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Callback function called when checkbox state changes
 * @property {string} [className] - Additional CSS class for container
 * @property {string} [labelClassName] - Additional CSS class for label
 * @property {number} [contentGap=8] - Gap between checkbox and content in pixels
 * @property {React.ReactNode} [children] - Additional content to render
 *
 * @param {CheckboxProps & React.InputHTMLAttributes<HTMLInputElement>} props
 */
export default function Checkbox({
  id = '',
  label = '',
  caption = '',
  tooltipText = '',
  checked = false,
  disabled = false,
  indeterminate = false,
  onChange = () => {},
  className = '',
  labelClassName = '',
  contentGap = 8,
  children = null,
  ...inputProps
}) {
  const uid = useId('checkbox');
  const inputRef = useRef();

  const inputId = id || uid;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [inputRef, indeterminate]);

  return (
    <div className={cn(styles.container, className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(!checked, e)}
        disabled={disabled}
        className={styles.input}
        {...inputProps}
      />
      <UiKit.Label forceMount htmlFor={inputId} className={styles.marker}>
        <svg viewBox="0 0 16 16" width="16" height="16">
          <rect width="16" height="16" rx="4" className={styles.backfill} />
          <rect
            x="0.5"
            y="0.5"
            width="15"
            height="15"
            rx="3.5"
            className={styles.outline}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.6046 4.44726C13.818 4.64479 13.8185 4.9821 13.6056 5.18023L6.26616 12.0108C6.07177 12.1917 5.76987 12.1891 5.57863 12.0049L2.3738 8.91756C2.16966 8.7209 2.16966 8.39403 2.3738 8.19737L3.22187 7.38041C3.41551 7.19387 3.72201 7.19387 3.91565 7.38041L5.88681 9.27928L12.0616 3.64313C12.2535 3.46796 12.5476 3.46898 12.7383 3.64547L13.6046 4.44726Z"
            className={styles.check}
          />
          <path
            d="M14.0001 8.00001C14.0001 8.5103 13.5867 8.92337 13.077 8.92337H8.92317C8.16936 8.92337 8.5098 8.92337 8.00009 8.92337C7.49038 8.92337 7.61988 8.92337 7.07701 8.92337H2.92317C2.41346 8.92337 2.00009 8.5103 2.00009 8.00001C2.00009 7.48972 2.41346 7.07722 2.92317 7.07722H7.07701L7.33342 7.07728C7.49749 7.07719 7.49038 7.07721 8.00009 7.07721C8.5098 7.07721 8.33342 7.07726 8.66676 7.07721L8.92317 7.07722H13.077C13.5876 7.07693 14.0001 7.48943 14.0001 8.00001Z"
            className={styles.line}
          />
        </svg>
      </UiKit.Label>
      <If test={label}>
        <UiKit.Grid
          templateColumns="1fr"
          gap={contentGap}
          className={styles.content}
        >
          <UiKit.Grid templateColumns="1fr">
            <UiKit.Label
              htmlFor={inputId}
              weight="normal"
              text={label}
              tooltipText={tooltipText}
              aria-disabled={disabled}
              className={cn(styles.label, labelClassName)}
            />
            <UiKit.Caption>{caption}</UiKit.Caption>
          </UiKit.Grid>
          {children}
        </UiKit.Grid>
      </If>
    </div>
  );
}
